import { useState } from 'react';
import { useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Alert, Stack, Text } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertCircle } from '@tabler/icons-react';
import { AnimatePresence } from 'framer-motion';
import { AuthCard } from 'src/auth/components/AuthCard';
import { AuthPageLayout } from 'src/auth/components/AuthPageLayout';
import { ApiError } from 'src/error/ApiError';
import { validateCompanyName } from 'src/common/utils/companyName';
import { WizardProgress } from './components/WizardProgress';
import { useSubmitOnboardingSignup } from './hooks/useSubmitOnboardingSignup';
import { applySubmissionError, OnboardingSignupFormValues } from './applySubmissionError';
import { WelcomeScreen } from './screens/WelcomeScreen';
import { FirstNameStep } from './screens/FirstNameStep';
import { ReviewStep } from './screens/ReviewStep';
import { BookCallIntroStep } from './screens/BookCallIntroStep';
import { BookCallScreen } from './screens/BookCallScreen';
import { ConfirmationScreen } from './screens/ConfirmationScreen';
import Config from 'src/common/Config';

type OnboardingInviteTokenResponse = {
  email: string;
  companyName: string;
  frn: string;
};

type WizardStep = 'firstName' | 'review';

const STEP_INDEX: Record<WizardStep, number> = {
  firstName: 0,
  review: 1,
};
const TOTAL_STEPS = 2;

export default function OnboardingSignupPage() {
  const { token } = useParams<{ token: string }>();
  const [welcomeComplete, setWelcomeComplete] = useState(false);

  const {
    data: invite,
    isLoading,
    error,
  } = useQuery<OnboardingInviteTokenResponse>({
    queryKey: ['public', 'onboarding-invite', token],
    enabled: !!token,
    retry: false,
    staleTime: Infinity,
  });

  if (!welcomeComplete) {
    return <WelcomeScreen onComplete={() => setWelcomeComplete(true)} />;
  }

  if (!token) {
    return <InvalidInviteState />;
  }

  if (isLoading) {
    return <AuthPageLayout withHeader isLoading />;
  }

  if (error || !invite) {
    return <InvalidInviteState status={error instanceof ApiError ? error.status : undefined} />;
  }

  return (
    <OnboardingSignupForm
      email={invite.email}
      companyName={invite.companyName}
      frn={invite.frn}
      token={token}
    />
  );
}

function InvalidInviteState({ status }: { status?: number }) {
  const isExpiredOrUsed = status === 410;

  return (
    <AuthPageLayout withHeader>
      <AuthCard>
        <Stack gap="md" align="center">
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
            title="This onboarding link cannot be used"
            w="100%"
          >
            {isExpiredOrUsed
              ? 'This link has expired or has already been used to set up an account.'
              : 'This link is invalid.'}
          </Alert>
          <Text size="sm" c="gray.6" ta="center">
            Please contact your Afternoon representative to request a new onboarding link.
          </Text>
        </Stack>
      </AuthCard>
    </AuthPageLayout>
  );
}

// The account exists from 'bookCall' onwards; the stage only decides what is shown
// after that point, never whether the submission happened.
type PostSubmitStage = 'form' | 'bookCallIntro' | 'bookCall' | 'confirmation';

function OnboardingSignupForm({
  email,
  companyName,
  frn,
  token,
}: {
  email: string;
  companyName: string;
  frn: string;
  token: string;
}) {
  const [stage, setStage] = useState<PostSubmitStage>('form');
  const [topLevelError, setTopLevelError] = useState<string | null>(null);
  const [ambiguousTimeout, setAmbiguousTimeout] = useState(false);
  const [step, setStep] = useState<WizardStep>('firstName');
  const [direction, setDirection] = useState<1 | -1>(1);
  const { submit, isLoading } = useSubmitOnboardingSignup(token);

  const form = useForm<OnboardingSignupFormValues>({
    mode: 'controlled',
    initialValues: {
      firstName: '',
      lastName: '',
      companyName,
    },
    validate: {
      firstName: v => (v.trim().length === 0 ? 'First name is required' : null),
      lastName: v => (v.trim().length === 0 ? 'Last name is required' : null),
      companyName: validateCompanyName,
    },
  });

  const handleSubmit = async () => {
    setTopLevelError(null);
    setAmbiguousTimeout(false);
    const values = form.getValues();
    try {
      await submit({
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        contractAgreed: true,
        companyName: values.companyName.trim(),
      });
      // Booking is skipped entirely when no Calendly link is configured, so the flow
      // never shows an empty embed.
      setStage(Config.getOnboardingCalendlyUrl() ? 'bookCallIntro' : 'confirmation');
    } catch (submitError) {
      applySubmissionError(submitError, form, setTopLevelError, () => setAmbiguousTimeout(true));
    }
  };

  const calendlyUrl = Config.getOnboardingCalendlyUrl();
  if (stage !== 'form' && stage !== 'confirmation' && calendlyUrl) {
    const values = form.getValues();
    return (
      <AuthPageLayout withHeader={false}>
        <AnimatePresence mode="wait">
          {stage === 'bookCallIntro' ? (
            <BookCallIntroStep key="bookCallIntro" onContinue={() => setStage('bookCall')} />
          ) : (
            <BookCallScreen
              key="bookCall"
              calendlyUrl={calendlyUrl}
              firstName={values.firstName.trim()}
              lastName={values.lastName.trim()}
              email={email}
              onBooked={() => setStage('confirmation')}
              onSkip={() => setStage('confirmation')}
            />
          )}
        </AnimatePresence>
      </AuthPageLayout>
    );
  }

  if (stage === 'confirmation') {
    return <ConfirmationScreen email={email} />;
  }

  const renderStep = () => {
    if (step === 'firstName') {
      return (
        <FirstNameStep
          key="firstName"
          form={form}
          direction={direction}
          currentStepIndex={STEP_INDEX.firstName}
          totalSteps={TOTAL_STEPS}
          onNext={() => {
            setDirection(1);
            setStep('review');
          }}
        />
      );
    }
    const values = form.getValues();
    return (
      <ReviewStep
        key="review"
        values={{
          firstName: values.firstName,
          lastName: values.lastName,
          companyName: values.companyName,
          frn,
          email,
        }}
        direction={direction}
        currentStepIndex={STEP_INDEX.review}
        totalSteps={TOTAL_STEPS}
        onBack={() => {
          setDirection(-1);
          setStep('firstName');
        }}
        onSubmit={handleSubmit}
        onEditField={() => {
          setDirection(-1);
          setStep('firstName');
        }}
        onCompanyNameChange={value => form.setFieldValue('companyName', value)}
        isSubmitting={isLoading}
        topLevelError={topLevelError}
        ambiguousTimeout={ambiguousTimeout}
      />
    );
  };

  return (
    <AuthPageLayout withHeader={false}>
      <WizardProgress currentStepIndex={STEP_INDEX[step]} totalSteps={TOTAL_STEPS} />
      <AnimatePresence mode="wait">{renderStep()}</AnimatePresence>
    </AuthPageLayout>
  );
}
